import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import { BrandOption, BrandType, ShopType } from 'utils/types';
import { apiService } from 'services/base.service';
import Select from 'components/base/select';
import { handleApiError, isFCShopCategory } from 'utils/functions';

type Props = {
  className?: string;
  hasSearch?: boolean;
  multiple?: boolean;
  brands: BrandOption[];
  disabled?: boolean;
  setBrands: (e: BrandOption[]) => void;
  selectFirstItem?: boolean;
  returnAllBrands?: (arg0: BrandType[]) => void;
};

const SelectBrand = ({
  multiple,
  brands,
  hasSearch,
  selectFirstItem,
  className,
  disabled,
  setBrands,
  returnAllBrands
}: Props) => {
  const { activeCokitchen, category } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [brandsList, setBrandsList] = useState<BrandOption[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const isFcShop = isFCShopCategory();

  useEffect(() => {
    if (isFcShop) {
      fetchShop();
    } else {
      fetchBrand();
    }
  }, [category, activeCokitchen, isFcShop]);

  const fetchBrand = async () => {
    if (!category) return;
    try {
      setLoading(true);
      const params = {
        cokitchen_id: activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params
      });
      const data: BrandType[] = res.data?.filter(
        (b: BrandType) => b.item_type === category.toUpperCase() && !b.archived
      );
      returnAllBrands?.(data);

      const brandArray = data.map((brand) => {
        return {
          value: brand.id,
          text: brand.name
        };
      });
      setBrandsList(brandArray);
      const brandId = searchParams.get('brand_id');
      if (brandId && brandId !== 'undefined') {
        setBrands?.([
          {
            text: data?.find((b) => b.id === searchParams.get('brand_id'))?.name as string,
            value: searchParams.get('brand_id') as string
          }
        ]);
      } else if (selectFirstItem) {
        searchParams.delete('brand_id');
        setSearchParams(searchParams);
        setBrands([brandArray[0]]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchShop = async () => {
    try {
      setLoading(true);

      const res = await apiService.get('shops');
      if (res.data.data.data) {
        const shopData = res.data.data.data.map((shop: ShopType) => {
          return {
            value: shop.id,
            text: shop.name
          };
        });
        setBrandsList(shopData);
      }
    } catch {
      setBrands([]);
      handleApiError('An error occured');
    } finally {
      setLoading(false);
    }
  };

  if (multiple)
    return (
      <div className={className}>
        <Select
          multiple
          disabled={disabled}
          hasSearch={hasSearch}
          loading={loading}
          values={brands.map((b) => b.value)}
          options={brandsList}
          placeholder={`Select ${isFcShop ? 'shop' : 'brand'}(s)`}
          onChange={(v) => setBrands(brandsList.filter((b) => v.includes(b.value)))}
        />
      </div>
    );

  return (
    <div className={className}>
      <Select
        disabled={disabled}
        hasSearch={hasSearch}
        loading={loading}
        value={brands.map((b) => b.value)?.[0]}
        options={brandsList}
        placeholder={`Select ${isFcShop ? 'shop' : 'brand'}(s)`}
        onChange={(v) => setBrands(brandsList.filter((b) => b.value === v))}
      />
    </div>
  );
};

export default SelectBrand;
